// src/components/VehicleCard.js
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Building2 } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { vehiclesAPI } from '../services/api';

const VehicleCard = ({ vehicle, isFavorite: initialIsFavorite = false, onFavoriteChange }) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [isFavorite, setIsFavorite] = useState(initialIsFavorite);
  const [busy, setBusy] = useState(false);

  const handleCardClick = () => {
    navigate(`/vehicle/${vehicle._id}`);
  };

  const handleFavoriteClick = async (e) => {
    // 🔑 IMPORTANT: don't trigger the card's onClick
    e.stopPropagation();

    if (!user) {
      navigate('/login');
      return;
    }

    try {
      setBusy(true);
      await vehiclesAPI.toggleFavorite(vehicle._id);
      setIsFavorite((prev) => {
        const next = !prev;
        if (onFavoriteChange) {
          onFavoriteChange(vehicle._id, next);
        }
        return next;
      });
    } catch (err) {
      console.error('Toggle favorite error from card:', err);
      // you can toast or alert here if you want
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-3 border border-app-border-light dark:border-app-border cursor-pointer hover:border-app-accent/60 transition-colors flex flex-col md:flex-row gap-3"
    >
      {/* Vehicle image */}
      <img
        src={vehicle.image}
        alt={`${vehicle.make} ${vehicle.model}`}
        className="w-full md:w-20 h-40 md:h-16 rounded-xl object-cover flex-shrink-0"
      />

      {/* Main info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </div>
            <div className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary truncate">
              {vehicle.mileage != null
                ? `${vehicle.mileage.toLocaleString()} mi`
                : 'Mileage N/A'}{' '}
              • {vehicle.type}
            </div>
          </div>

          {/* Heart button */}
          <button
            type="button"
            onClick={handleFavoriteClick}
            disabled={busy}
            className={`p-1.5 rounded-full border shadow-sm ${
              isFavorite
                ? 'bg-red-500/15 border-red-400'
                : 'bg-app-bg-quaternary-light dark:bg-app-bg-quaternary border-app-border-light dark:border-app-border'
            }`}
            title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <Heart
              size={16}
              className={
                isFavorite ? 'fill-red-500 text-red-500' : 'text-app-text-secondary-light dark:text-app-text-secondary'
              }
            />
          </button>
        </div>

        <div className="mt-1 text-[11px] text-app-accent">
          {vehicle.price != null
            ? `$${vehicle.price.toLocaleString()}`
            : 'Price N/A'}
        </div>

        {/* Dealership link */}
        {vehicle.dealer && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              const dealerId = typeof vehicle.dealer === 'object' ? vehicle.dealer._id : vehicle.dealer;
              navigate(`/dealer/${dealerId}/info`);
            }}
            className="mt-1 flex items-center gap-1 text-[10px] text-customer-teal hover:text-customer-teal-hover transition-colors"
          >
            <Building2 size={12} />
            <span className="truncate">
              {typeof vehicle.dealer === 'object' ? vehicle.dealer.name : 'View Dealership'}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default VehicleCard;
